// Los datos del producto como UNA lista de campos (ADR-2026-10-15-los-datos-del-producto-son-una-lista):
// las medidas (que se guardan aparte, en `productos.medidas`, porque se calculan) y las
// características (en `variantes.atributos`) se ven y se suman igual: "Alto 90 cm",
// "Material: roble". Acá lo que decide qué fila es cuál. Puro.
import type { Medidas, UnidadMedida } from '../../../../lib/medidas';

export type ClaveDeMedida = 'alto' | 'ancho' | 'profundidad' | 'espesor' | 'peso_kg';

export const MEDIDAS_EN_ORDEN: readonly ClaveDeMedida[] = ['alto', 'ancho', 'profundidad', 'espesor', 'peso_kg'];
export const ETIQUETA_DE_MEDIDA: Record<ClaveDeMedida, string> = {
  alto: 'Alto',
  ancho: 'Ancho',
  profundidad: 'Profundidad',
  espesor: 'Espesor',
  peso_kg: 'Peso',
};
const PRINCIPALES: readonly ClaveDeMedida[] = ['alto', 'ancho', 'profundidad'];

const sinTildes = (s: string): string =>
  s
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/** "Alto", "PROFUNDIDAD", "peso" → la clave de la medida; cualquier otro nombre, null. */
export function medidaDelNombre(nombre: string): ClaveDeMedida | null {
  const n = sinTildes(nombre);
  if (!n) return null;
  return MEDIDAS_EN_ORDEN.find((k) => sinTildes(ETIQUETA_DE_MEDIDA[k]) === n) ?? null;
}

/** Las filas de medidas a la vista: las medidas son campos como los demás, así que están las
 *  cargadas y, si una toma las exige, alto, ancho y profundidad aunque estén vacías
 *  (ADR-2026-10-15-las-medidas-son-campos-como-los-demas). En el orden de `MEDIDAS_EN_ORDEN`. */
export function filasDeMedidasIniciales(medidas: Medidas | null | undefined, exige: boolean): ClaveDeMedida[] {
  return MEDIDAS_EN_ORDEN.filter((k) => {
    const v = medidas?.[k];
    return (typeof v === 'number' && v > 0) || (exige && PRINCIPALES.includes(k));
  });
}

export const esPrincipal = (k: ClaveDeMedida): boolean => PRINCIPALES.includes(k);

const A_CM: Record<UnidadMedida, number> = { mm: 0.1, cm: 1, m: 100 };

/** Un valor de una unidad a otra, con hasta 4 decimales. */
export function convertirMedida(valor: number | null | undefined, de: UnidadMedida, a: UnidadMedida): number | null {
  if (typeof valor !== 'number' || !Number.isFinite(valor) || valor <= 0) return null;
  return Number(((valor * A_CM[de]) / A_CM[a]).toFixed(4));
}

/** La unidad es UNA para todas las medidas: cambiarla convierte alto, ancho, profundidad y
 *  espesor a la nueva (el tamaño real no cambia). El peso va en kg y no se toca. */
export function cambiarUnidad(medidas: Medidas | null | undefined, a: UnidadMedida): Medidas {
  const de = medidas?.unidad ?? 'cm';
  return {
    ...(medidas ?? {}),
    unidad: a,
    alto: convertirMedida(medidas?.alto, de, a),
    ancho: convertirMedida(medidas?.ancho, de, a),
    profundidad: convertirMedida(medidas?.profundidad, de, a),
    espesor: convertirMedida(medidas?.espesor, de, a),
  };
}

/** Sugerencias para el nombre de un campo nuevo: las medidas y las características típicas. */
export const SUGERENCIAS_DE_CAMPO: readonly string[] = [
  ...MEDIDAS_EN_ORDEN.map((k) => ETIQUETA_DE_MEDIDA[k]),
  'Material',
  'Color',
  'Terminación',
  'Tapizado',
  'Capacidad',
  'Volumen',
  'Potencia',
  'Batería',
  'Presión',
  'Garantía',
];
